// VOY-571: the flight route line on the checkout success page.
//
// Flight item_data reaches order_items in two shapes: the app's flight objects, whose slice
// endpoints are strings (an IATA code or a city name), and raw Duffel offers, whose endpoints are
// place objects (iata_code, city_name, name, city.name). The line shows the outbound slice
// (slices[0]) as "City (IATA)" and marks round trips and multi-city itineraries, because pairing
// the first origin with the last destination turns a round trip into "Buenos Aires → Buenos Aires".

type Place = {
  iata_code?: string | null
  iata_city_code?: string | null
  city_name?: string | null
  name?: string | null
  city?: { name?: string | null } | null
}

type Endpoint = string | Place | null | undefined

type Segment = { departureAirport?: unknown; arrivalAirport?: unknown }

export type FlightItemData = {
  slices?: Array<{ origin?: Endpoint; destination?: Endpoint; segments?: Segment[] }>
  origin?: Endpoint
  destination?: Endpoint
}

type EndpointParts = { city?: string; code?: string }

const IATA = /^[A-Z]{3}$/

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined
}

/** City and IATA code of a slice endpoint, from a string or a Duffel place object. */
function endpointParts(value: Endpoint, segmentAirport?: unknown): EndpointParts {
  const fallbackCode = text(segmentAirport)
  const segmentCode = fallbackCode && IATA.test(fallbackCode) ? fallbackCode : undefined
  if (typeof value === 'string') {
    const s = value.trim()
    if (!s) return { code: segmentCode }
    return IATA.test(s) ? { code: s } : { city: s, code: segmentCode }
  }
  if (value && typeof value === 'object') {
    const code = text(value.iata_code) ?? text(value.iata_city_code) ?? segmentCode
    const city = text(value.city_name) ?? text(value.city?.name) ?? text(value.name)
    return { city, code }
  }
  return { code: segmentCode }
}

function formatEndpoint(parts: EndpointParts): string | undefined {
  if (parts.city && parts.code) return `${parts.city} (${parts.code})`
  return parts.city ?? parts.code
}

/** Same place: by IATA code when both sides have one, otherwise by city name. */
function samePlace(a: EndpointParts, b: EndpointParts): boolean {
  if (a.code && b.code && a.code === b.code) return true
  if (a.city && b.city) return a.city.toLowerCase() === b.city.toLowerCase()
  return false
}

export type TripType = 'one_way' | 'round_trip' | 'multi_city'

export const TRIP_TYPE_LABELS: Record<Exclude<TripType, 'one_way'>, string> = {
  round_trip: 'Round trip',
  multi_city: 'Multi-city',
}

export interface FlightRoute {
  origin: string
  destination: string
  tripType: TripType
}

/** The outbound route and trip type of a flight item, or null when no usable endpoints exist. */
export function getFlightRoute(itemData: FlightItemData | null | undefined): FlightRoute | null {
  const slices = Array.isArray(itemData?.slices) ? itemData.slices : []

  if (slices.length > 0) {
    const sliceParts = slices.map((slice) => {
      const segments = Array.isArray(slice?.segments) ? slice.segments : []
      return {
        origin: endpointParts(slice?.origin, segments[0]?.departureAirport),
        destination: endpointParts(slice?.destination, segments[segments.length - 1]?.arrivalAirport),
      }
    })
    const first = sliceParts[0]
    const origin = formatEndpoint(first.origin)
    const destination = formatEndpoint(first.destination)
    if (origin && destination) {
      let tripType: TripType = 'one_way'
      if (sliceParts.length === 2) {
        const back = sliceParts[1]
        const mirrors = samePlace(back.origin, first.destination) && samePlace(back.destination, first.origin)
        tripType = mirrors ? 'round_trip' : 'multi_city'
      } else if (sliceParts.length > 2) {
        tripType = 'multi_city'
      }
      return { origin, destination, tripType }
    }
  }

  const fallbackOrigin = endpointParts(itemData?.origin)
  const fallbackDestination = endpointParts(itemData?.destination)
  const origin = formatEndpoint(fallbackOrigin)
  const destination = formatEndpoint(fallbackDestination)
  if (origin && destination && !samePlace(fallbackOrigin, fallbackDestination)) {
    return { origin, destination, tripType: 'one_way' }
  }
  return null
}

/** The Booking Details line for a flight item, e.g. "Buenos Aires (EZE) → Tokyo (NRT) · Round trip". */
export function formatFlightRoute(itemData: FlightItemData | null | undefined): string {
  const route = getFlightRoute(itemData)
  if (!route) return 'Flight Booking'
  const line = `${route.origin} → ${route.destination}`
  return route.tripType === 'one_way' ? line : `${line} · ${TRIP_TYPE_LABELS[route.tripType]}`
}
